import {
  AdminPage,
  Alert,
  Avatar,
  Card,
  Col,
  DetailDescriptions,
  Row,
  Space,
  Spin,
  Statistic,
  StatusTag,
  Tabs,
  Tag,
  Typography,
} from '@wanjianshi/admin-ui';
import { formatDateTime, formatNumber } from '@wanjianshi/shared-utils';
import { lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { ProjectAssetsPanel, ProjectCanvasDetailsPanel } from '@/features/projects/ProjectDetailPanels';
import type { ProjectDetail } from '@/features/projects/projectDetail';
import { useAdminRecord } from '@/hooks/useAdminResource';

const RoutingAdminPage = lazy(() => import('@/pages/RoutingAdmin/RoutingAdminPage'));

export function ProjectDetailPage() {
  const { projectId } = useParams();
  const numericProjectId = Number(projectId);
  const validProjectId = Number.isInteger(numericProjectId) && numericProjectId > 0;
  const resource = useAdminRecord<ProjectDetail>(validProjectId ? `/admin/projects/${numericProjectId}` : null);
  const project = resource.data;

  if (!validProjectId) {
    return <AdminPage title="项目详情"><Alert showIcon type="error" message="项目 ID 无效" /></AdminPage>;
  }

  return (
    <AdminPage
      title={project?.name || `项目 #${numericProjectId}`}
      description="集中查看生成任务、积分消耗、资产数据和画布内部结构"
      extra={project ? (
        <Space>
          <Tag color={project.project_type === 'team' ? 'blue' : 'default'}>
            {project.project_type === 'team' ? '团队项目' : '个人项目'}
          </Tag>
          <StatusTag value="active" label="正常" />
        </Space>
      ) : undefined}
    >
      {resource.error ? (
        <Alert showIcon type="error" message={resource.error} action={<a onClick={() => void resource.reload()}>重试</a>} />
      ) : null}

      {!project && resource.loading ? <div className="project-detail-loading"><Spin size="large" /></div> : null}

      {project ? (
        <>
          {project.anomalies.length ? (
            <Alert
              showIcon
              type="warning"
              message="项目归属存在异常"
              description={project.anomalies.join('、')}
            />
          ) : null}

          <Card className="project-detail-overview-card">
            <div className="project-detail-overview-heading">
              <Space size={14} align="start">
                <Avatar shape="square" size={64} src={project.cover || undefined}>{project.name.slice(0, 1)}</Avatar>
                <div>
                  <Typography.Title level={4}>{project.name}</Typography.Title>
                  <Typography.Text type="secondary">{project.description || '暂无项目描述'}</Typography.Text>
                </div>
              </Space>
              <Typography.Text type="secondary">项目 ID：{project.id}</Typography.Text>
            </div>

            <Row gutter={[16, 16]} className="project-detail-metrics">
              <Col xs={12} md={6}><Statistic title="生成任务" value={project.task_count} /></Col>
              <Col xs={12} md={6}><Statistic title="资产数量" value={project.asset_count} /></Col>
              <Col xs={12} md={6}><Statistic title="画布数量" value={project.canvas_count} /></Col>
              <Col xs={12} md={6}><Statistic title="画布节点" value={project.node_count} /></Col>
            </Row>

            <DetailDescriptions
              className="project-detail-base-info"
              column={{ xs: 1, sm: 2, lg: 4 }}
              items={[
                { key: 'owner', label: '所属用户', children: project.owner_nickname || project.owner_username || '—' },
                { key: 'owner_id', label: '用户 ID', children: String(project.owner_user_id) },
                { key: 'mobile', label: '用户手机号', children: project.owner_mobile_masked || '—' },
                { key: 'email', label: '用户邮箱', children: project.owner_email_masked || '—' },
                { key: 'team', label: '所属团队', children: project.project_type === 'team' ? project.team_name || `团队 #${project.team_id}` : '个人项目' },
                { key: 'folder', label: '项目文件夹 ID', children: project.folder_id ? String(project.folder_id) : '—' },
                { key: 'tasks', label: '任务状态', children: `成功 ${formatNumber(project.succeeded_task_count)} / 进行中 ${formatNumber(project.running_task_count)} / 失败 ${formatNumber(project.failed_task_count)}` },
                { key: 'canvas_structure', label: '画布结构', children: `节点 ${formatNumber(project.node_count)} / 连线 ${formatNumber(project.connection_count)} / 分组 ${formatNumber(project.group_count)}` },
                { key: 'created', label: '创建时间', children: formatDateTime(project.created_at) },
                { key: 'updated', label: '更新时间', children: formatDateTime(project.updated_at) },
              ]}
            />
          </Card>

          <Card className="project-detail-tabs-card">
            <Tabs
              destroyOnHidden
              items={[
                {
                  key: 'tasks',
                  label: `生成任务（${formatNumber(project.task_count)}）`,
                  children: (
                    <Suspense fallback={<div className="project-detail-loading"><Spin tip="正在加载生成任务…" /></div>}>
                      <RoutingAdminPage view="tasks" projectId={numericProjectId} embedded />
                    </Suspense>
                  ),
                },
                {
                  key: 'assets',
                  label: `资产数据（${formatNumber(project.asset_count)}）`,
                  children: <ProjectAssetsPanel projectId={numericProjectId} kindCounts={project.asset_kind_counts} />,
                },
                {
                  key: 'canvas-details',
                  label: `画布详细信息（${formatNumber(project.canvas_count)}）`,
                  children: <ProjectCanvasDetailsPanel projectId={numericProjectId} />,
                },
              ]}
            />
          </Card>
        </>
      ) : null}
    </AdminPage>
  );
}
